import { createContext, useContext, useEffect, useRef, useState } from "react";
import { fileUrl } from "@/lib/api";
import { Play, Pause, X, SpeakerHigh } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";

const AudioContext = createContext(null);
export const useAudio = () => useContext(AudioContext);

export function AudioProvider({ children }) {
  const [track, setTrack] = useState(null); // {fileId, title, creator}
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(new Audio());

  const play = async (t) => {
    const audio = audioRef.current;
    if (track && track.fileId === t.fileId) {
      audio.paused ? audio.play() : audio.pause();
      return;
    }
    setTrack(t);
    // Stream directly (same-origin, credentialed) so the browser can seek via range requests
    audio.src = fileUrl(t.fileId);
    audio.currentTime = 0;
    try {
      await audio.play();
    } catch {
      setTrack(null);
    }
  };

  const close = () => {
    audioRef.current.pause();
    setTrack(null);
    setPlaying(false);
  };

  useEffect(() => {
    const audio = audioRef.current;
    const onTime = () => {
      setProgress(audio.currentTime);
      setDuration(audio.duration || 0);
    };
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("ended", onPause);
    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("ended", onPause);
    };
  }, []);

  const fmt = (s) => {
    if (!s || isNaN(s)) return "0:00";
    const m = Math.floor(s / 60);
    const ss = Math.floor(s % 60).toString().padStart(2, "0");
    return `${m}:${ss}`;
  };

  const seek = (e) => {
    const audio = audioRef.current;
    const pct = e.target.value / 100;
    audio.currentTime = pct * (audio.duration || 0);
  };

  return (
    <AudioContext.Provider value={{ track, playing, play, close }}>
      {children}
      <AnimatePresence>
        {track && (
          <motion.div
            data-testid="global-audio-player"
            initial={{ y: 120 }}
            animate={{ y: 0 }}
            exit={{ y: 120 }}
            transition={{ type: "spring", stiffness: 260, damping: 30 }}
            className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[120] w-[92%] max-w-2xl glass border border-border rounded-md px-4 py-3 flex items-center gap-4"
          >
            <div className="h-11 w-11 shrink-0 bg-primary/20 border border-primary/40 flex items-center justify-center">
              <SpeakerHigh size={20} weight="fill" className="text-primary" />
            </div>
            <button
              data-testid="audio-toggle"
              onClick={() => (playing ? audioRef.current.pause() : audioRef.current.play())}
              className="h-10 w-10 shrink-0 bg-primary text-primary-foreground flex items-center justify-center rounded-full transition-transform active:scale-90"
            >
              {playing ? <Pause size={18} weight="fill" /> : <Play size={18} weight="fill" />}
            </button>
            <div className="flex-1 min-w-0">
              <p className="font-accent text-sm font-bold truncate">{track.title}</p>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-muted-foreground tabular-nums">{fmt(progress)}</span>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={duration ? (progress / duration) * 100 : 0}
                  onChange={seek}
                  className="flex-1 h-1 accent-primary cursor-pointer"
                  data-testid="audio-seek"
                />
                <span className="text-[10px] text-muted-foreground tabular-nums">{fmt(duration)}</span>
              </div>
            </div>
            <button data-testid="audio-close" onClick={close} className="text-muted-foreground hover:text-foreground transition-colors">
              <X size={18} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </AudioContext.Provider>
  );
}
